import {
    ModifyUserPreferencesModel,
    preferencesApi,
    TicketSearchView,
    UserPreferences,
} from "../api/preferences";
import type { ProjectId } from "../api/projects";
import { defineStore } from "pinia";
import { useQueryCache } from "@pinia/colada";
import { ensureQueryData } from "@/queries/utils";

export const PREFERENCE_KEYS = {
    root: ["preferences"] as const,
    me: () => [...PREFERENCE_KEYS.root, "me"] as const,
};

const preferencesQueryOptions = {
    key: PREFERENCE_KEYS.me(),
    query: () => preferencesApi.getPreferences(),
    staleTime: 5 * 60_000,
};

export type PreferencesState = {
    currentSearchView: TicketSearchView | undefined;
};

export const usePreferencesStore = defineStore("preferences", {
    state: (): PreferencesState => ({
        currentSearchView: undefined,
    }),
    actions: {
        async loadPreferences(force = false) {
            const prefs = await ensureQueryData(preferencesQueryOptions, force);
            if (!prefs) {
                return;
            }

            if (this.$state.currentSearchView == undefined) {
                this.$state.currentSearchView = prefs.defaultSearchView;
            }

            if (prefs.timeZone == null) {
                const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (detected) {
                    await this.modifyUserPreferences({ timeZone: detected });
                }
            }
        },

        async resetToDefaults() {
            const prefs = await preferencesApi.resetToDefaults();
            useQueryCache().setQueryData(PREFERENCE_KEYS.me(), { ...prefs });
        },

        async modifyUserPreferences(model: ModifyUserPreferencesModel) {
            const result = await preferencesApi.modifyUserPreferences(model);
            if (result) {
                if (this.prefs == undefined) {
                    await this.loadPreferences(true);
                    return;
                }

                useQueryCache().setQueryData<UserPreferences>(PREFERENCE_KEYS.me(), (prefs) => ({
                    ...(prefs as UserPreferences),
                    ...model,
                }));
                if (model.defaultSearchView != undefined) {
                    this.$state.currentSearchView = model.defaultSearchView;
                }
            }
        },

        toggleSearchView() {
            if (this.$state.currentSearchView == TicketSearchView.TableView) {
                this.$state.currentSearchView = TicketSearchView.CompactView;
            } else {
                this.$state.currentSearchView = TicketSearchView.TableView;
            }
        },

        async togglePinnedProject(id: ProjectId) {
            const current = this.pinnedProjectIds;
            const next = current.includes(id) ? current.filter((p) => p !== id) : [...current, id];
            await this.modifyUserPreferences({ pinnedProjects: next.join(",") });
        },

        async toggleRadarHiddenProject(id: ProjectId) {
            const current = this.radarHiddenProjectIds;
            const next = current.includes(id) ? current.filter((p) => p !== id) : [...current, id];
            await this.modifyUserPreferences({ radarHiddenProjects: next.join(",") });
        },
    },
    getters: {
        prefs(): UserPreferences | undefined {
            return useQueryCache().getQueryData<UserPreferences>(PREFERENCE_KEYS.me());
        },
        // Pinned project ids in pin order; empty when nothing is pinned.
        pinnedProjectIds(): ProjectId[] {
            return (this.prefs?.pinnedProjects ?? "")
                .split(",")
                .filter((s) => s.length > 0) as ProjectId[];
        },
        isProjectPinned(): (id: ProjectId) => boolean {
            return (id: ProjectId) => this.pinnedProjectIds.includes(id);
        },
        // Projects this user hides from the release radar; empty when none.
        radarHiddenProjectIds(): ProjectId[] {
            return (this.prefs?.radarHiddenProjects ?? "")
                .split(",")
                .filter((s) => s.length > 0) as ProjectId[];
        },
    },
});
